import React from 'react';
import { resolveNotificationMessage } from '../utils/notificationMessages';
import { getNotificationVisual } from '../utils/notificationVisuals';
import { Box, Typography, IconButton } from '@mui/material';
import { AccessTime as ClockIcon, Close as DeleteIcon, NotificationsOff as EmptyIcon } from '@mui/icons-material';
import moment from 'moment';
import EmptyState from './bento/EmptyState';
import { DIVIDER, TEXT_DARK, TEXT_SUBTLE, BG_TINT, BG_PURPLE_TINT } from './bento/bentoTokens';

const NotificationList = ({
  notifications = [],
  onNotificationClick,
  onDeleteNotification,
}) => {
  if (notifications.length === 0) {
    return (
      <EmptyState
        icon={
          <Box sx={{ width: 56, height: 56, borderRadius: '16px', background: BG_PURPLE_TINT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <EmptyIcon sx={{ color: 'secondary.main' }} />
          </Box>
        }
        message={
          <>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: TEXT_DARK, mb: 0.5 }}>No notifications</Typography>
            <Typography sx={{ fontSize: 13, color: TEXT_SUBTLE }}>You're all caught up! Check back later.</Typography>
          </>
        }
      />
    );
  }

  return (
    <Box>
      {notifications.map((notification) => {
        const { Icon, badgeLabel, color, bg, border } = getNotificationVisual(notification);
        return (
          <Box
            key={notification.id}
            onClick={() => onNotificationClick?.(notification)}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '14px',
              p: '14px 22px',
              borderTop: `1px solid ${DIVIDER}`,
              cursor: onNotificationClick ? 'pointer' : 'default',
              '&:hover': { backgroundColor: BG_TINT },
            }}
          >
            <Box sx={{ width: 40, height: 40, flex: 'none', mt: '1px', borderRadius: '12px', background: bg, border: `1px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon sx={{ fontSize: 18, color }} />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: TEXT_DARK }}>
                      {resolveNotificationMessage(notification) || notification.message}
                    </Typography>
                    <Box component="span" sx={{ fontSize: 11, fontWeight: 700, color, background: bg, px: '8px', py: '2px', borderRadius: '999px', whiteSpace: 'nowrap' }}>
                      {badgeLabel}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', mt: '5px' }}>
                    <ClockIcon sx={{ fontSize: 11, color: TEXT_SUBTLE }} />
                    <Typography sx={{ fontFamily: "'Space Grotesk', monospace", fontSize: 11.5, color: TEXT_SUBTLE, fontWeight: 500 }}>
                      {moment(notification.timestamp).format('MMM DD, YYYY · HH:mm')}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 'none' }}>
                  <Typography sx={{ fontFamily: "'Space Grotesk', monospace", fontSize: 11.5, color: '#b3aebd', fontWeight: 500, whiteSpace: 'nowrap' }}>
                    {moment(notification.timestamp).fromNow()}
                  </Typography>
                  {onDeleteNotification && (
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); onDeleteNotification(notification.id); }}
                      sx={{
                        width: 28, height: 28, borderRadius: '8px', border: `1px solid ${DIVIDER}`,
                        '&:hover': { backgroundColor: '#fff1f2', borderColor: '#fca5a5' },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 14, color: '#c3bfcd' }} />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default NotificationList;
